/**
 * Production adapter for `interpolate_structure`: the SAME calls the
 * right-hand panel's Interpolation operation makes —
 * `Environment.handleInterpolateAllSlices(roi)` for "All empty" and
 * `Environment.handleInterpolateSingleSlice(roi, mainView.position[2])` for
 * "Current" — so the agent fills exactly what the panel would, with the
 * same 'interpolation' undo step.
 *
 * The pre-card checks only READ: the structure's cached slice pixels
 * (`texture.pixels`, drawn = max > 128, the engine's threshold) and the
 * engine's own slice lookup for a position (snap to the image slice centre,
 * then floor in texture-voxel space, as `ContourOperations` does). Browser
 * glue covered by the manual demo; the logic is unit-tested through fakes.
 */
import { vec3 } from 'gl-matrix';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { projectStructureFacts } from '../core/engineStructureFacts';
import type { InterpolateDeps } from './toolRunner';

/** The engine's "this slice holds a contour" threshold. */
const DRAWN_SDF_THRESHOLD = 128;

export function useInterpolateStructureDeps(): () => Omit<
    InterpolateDeps,
    'pendingMutationExists'
> {
    const env = useRecoilValue(environmentState);
    const mainView = useRecoilValue(mainViewState);
    const structureSet = useRecoilValue(currentStructureSetState);

    return useCallback(() => {
        const requireRoi = (roiNumber: number) => {
            const roi = env?.getRoi(roiNumber);
            if (!env || !roi) {
                throw new Error('the structure is no longer loaded');
            }
            return { env, roi };
        };
        return {
            structures: projectStructureFacts(structureSet, env),
            canModify: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.canModify ?? false,
            // Read live: `env` is the mutable singleton.
            currentContextId: () => env?.structureSet?.sopInstanceUID,
            editVersion: (roiNumber: number) =>
                env?.getRoi(roiNumber)?.sdfVersion,
            // The panel's "Current" scope reads the same coordinate.
            currentSlicePositionMm: () => mainView?.position[2],
            drawnSlices: (roiNumber: number) => {
                const texture = env?.getRoi(roiNumber)?.texture;
                if (!texture?.signedDistanceField) {
                    return undefined;
                }
                return texture.pixels.map((slice) => {
                    let max = 0;
                    for (let i = 0; i < slice.length; i++) {
                        if (slice[i] > max) {
                            max = slice[i];
                        }
                    }
                    return max > DRAWN_SDF_THRESHOLD;
                });
            },
            sliceIndexAt: (roiNumber: number, slicePositionMm: number) => {
                const texture = env?.getRoi(roiNumber)?.texture;
                const imageGrid = env?.image?.parent.grid;
                if (!texture || !imageGrid) {
                    return undefined;
                }
                const sliceCentre = imageGrid.snapPositionToSliceCenter(
                    vec3.fromValues(0, 0, slicePositionMm)
                );
                const z = Math.floor(
                    texture.textureGrid.toVoxelSpace(sliceCentre)[2]
                );
                return texture.textureGrid.includesVoxel(
                    vec3.fromValues(0, 0, z)
                )
                    ? z
                    : undefined;
            },
            volumeCm3: (roiNumber: number) =>
                env?.getRoiVolumeCm3(roiNumber) ?? Number.NaN,
            interpolate: {
                allEmpty: (roiNumber: number) => {
                    const { env: e, roi } = requireRoi(roiNumber);
                    e.handleInterpolateAllSlices(roi);
                },
                currentSlice: (roiNumber: number, slicePositionMm: number) => {
                    const { env: e, roi } = requireRoi(roiNumber);
                    e.handleInterpolateSingleSlice(roi, slicePositionMm);
                },
            },
        };
    }, [env, mainView, structureSet]);
}
